// Nostr publishing layer (VISION.md §8) — the open, no-mandatory-authority
// distribution for shared annotations.
//
// Key handling: we NEVER touch the user's secret key. Publishing goes through a
// NIP-07 browser signer (Alby, nos2x, a Nostr wallet's extension) which holds
// the nsec and signs on request. The app only ever sees the public key and the
// finished signature. If no signer is present, publishing is unavailable and we
// say so — nothing is stubbed or faked.
//
// Interoperability: highlights are published as NIP-84 events (kind 9802), the
// actual Nostr standard for highlights, so a PageVoice highlight is a first-
// class object in any Nostr highlight client — not a proprietary format.

import type { Highlight } from "@/types/highlight";

// Minimal NIP-07 provider shape.
interface Nip07 {
  getPublicKey(): Promise<string>;
  signEvent(e: UnsignedEvent): Promise<SignedEvent>;
}
declare global {
  interface Window {
    nostr?: Nip07;
  }
}

export interface UnsignedEvent {
  kind: number;
  created_at: number;
  tags: string[][];
  content: string;
  pubkey?: string;
}
export interface SignedEvent extends UnsignedEvent {
  id: string;
  sig: string;
  pubkey: string;
}

export interface BookMeta {
  title: string;
  author?: string;
  isbn?: string;
  /** Gutenberg / cloud id, used as a source anchor when no ISBN. */
  sourceId?: string;
}

/** A small default relay set. The user can override later (VISION.md §2:
 *  no mandatory authority — personal/community relays are all valid). */
export const DEFAULT_RELAYS = [
  "wss://relay.damus.io",
  "wss://nos.lol",
  "wss://relay.nostr.band",
];

/** True when a NIP-07 signer (wallet/extension) is available to sign events. */
export function hasNostrSigner(): boolean {
  return typeof window !== "undefined" && !!window.nostr;
}

function bookSourceTags(book: BookMeta): string[][] {
  const tags: string[][] = [];
  // NIP-73 external content ids where we have them; otherwise a plain book tag.
  if (book.isbn) tags.push(["i", `isbn:${book.isbn.replace(/[^0-9Xx]/g, "")}`]);
  else if (book.sourceId) tags.push(["i", `pagevoice:${book.sourceId}`]);
  tags.push(["book", [book.title, book.author].filter(Boolean).join(" — ")]);
  return tags;
}

/** NIP-84 highlight event (kind 9802). content = the highlighted passage. */
export function buildHighlightEvent(h: Highlight, book: BookMeta): UnsignedEvent {
  const tags: string[][] = [
    ...bookSourceTags(book),
    ["client", "pagevoice"],
    ["alt", `Highlight from “${book.title}”`],
  ];
  if (h.note) tags.push(["comment", h.note]);
  if (h.context) tags.push(["context", h.context]);
  for (const t of h.tags ?? []) tags.push(["t", t]);
  return {
    kind: 9802,
    created_at: Math.floor((h.createdAt || Date.now()) / 1000),
    tags,
    content: h.text,
  };
}

/** Book review as a kind-1 note anchored to the book, with a rating tag. */
export function buildReviewEvent(h: Highlight, book: BookMeta): UnsignedEvent {
  const stars = h.rating ? "★".repeat(h.rating) + "☆".repeat(5 - h.rating) : "";
  const header = `Review of “${book.title}”${book.author ? ` by ${book.author}` : ""}`;
  const content = [header, stars && `${stars} (${h.rating}/5)`, "", h.note || ""]
    .filter((x) => x !== undefined)
    .join("\n")
    .trim();
  const tags: string[][] = [
    ...bookSourceTags(book),
    ["client", "pagevoice"],
    ["t", "bookreview"],
    ["alt", header],
  ];
  if (h.rating) tags.push(["rating", String(h.rating), "5"]);
  return { kind: 1, created_at: Math.floor(Date.now() / 1000), tags, content };
}

/** The exact unsigned event that WOULD be published (VISION.md §13 preview). */
export function previewEvent(h: Highlight, book: BookMeta): UnsignedEvent {
  return h.kind === "review"
    ? buildReviewEvent(h, book)
    : buildHighlightEvent(h, book);
}

/** Publish an annotation: sign via the NIP-07 signer, then broadcast to relays.
 *  Throws if no signer is present. Returns the relays that accepted it. */
export async function publishAnnotation(
  h: Highlight,
  book: BookMeta,
  relays: string[] = DEFAULT_RELAYS,
): Promise<{ event: SignedEvent; accepted: string[] }> {
  if (!window.nostr) throw new Error("No Nostr signer available");
  const unsigned = previewEvent(h, book);
  const pubkey = await window.nostr.getPublicKey();
  const signed = await window.nostr.signEvent({ ...unsigned, pubkey });
  const accepted = await broadcast(signed, relays);
  if (accepted.length === 0) throw new Error("No relay accepted the event");
  return { event: signed, accepted };
}

/** Send a signed event to each relay over a short-lived websocket. Resolves
 *  with the relays that acknowledged an OK. Best-effort per relay. */
function broadcast(event: SignedEvent, relays: string[]): Promise<string[]> {
  return Promise.all(
    relays.map(
      (url) =>
        new Promise<string | null>((resolve) => {
          let done = false;
          const finish = (ok: string | null) => {
            if (done) return;
            done = true;
            try { ws.close(); } catch { /* ignore */ }
            resolve(ok);
          };
          const ws = new WebSocket(url);
          const timer = setTimeout(() => finish(null), 6000);
          ws.onopen = () => ws.send(JSON.stringify(["EVENT", event]));
          ws.onmessage = (m) => {
            try {
              const msg = JSON.parse(m.data as string);
              if (msg[0] === "OK" && msg[1] === event.id) {
                clearTimeout(timer);
                finish(msg[2] ? url : null);
              }
            } catch { /* ignore malformed */ }
          };
          ws.onerror = () => { clearTimeout(timer); finish(null); };
        }),
    ),
  ).then((res) => res.filter((r): r is string => !!r));
}
